import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogTitle,
} from '@codes/ui-kit/components/ui/alert-dialog';
import { Avatar } from '@codes/ui-kit/components/ui/avatar';
import { Button } from '@codes/ui-kit/components/ui/button';
import { Card } from '@codes/ui-kit/components/ui/card';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@codes/ui-kit/components/ui/dropdown-menu';
import { Image } from '@codes/ui-kit/components/ui/image';
import {
    Sheet,
    SheetClose,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
} from '@codes/ui-kit/components/ui/sheet';
import { toast } from '@codes/ui-kit/components/ui/use-toast';
import { ChatView } from '@lemoncloud/ssocio-chatbots-api';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, EllipsisVertical, Loader2, MessageSquare, Trash2, X } from 'lucide-react';

import { useAgent } from '@codes/agents';
import { AiLogoDarkImage, AiLogoImage } from '@codes/assets';
import { useAgentChats, useDeleteChat } from '@codes/chatbots';
import { formatDate } from '@codes/utils';

import { updateAllListCaches } from '../utils/cache-utils';

interface ChatListSheetProps {
    agentId: string | null;
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    onChatSelect?: (chatId: string) => void;
    workspaceId?: string;
    projectId?: string;
}

export const ChatListSheet: React.FC<ChatListSheetProps> = ({
    agentId,
    isOpen,
    onOpenChange,
    onChatSelect,
    workspaceId,
    projectId,
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [currentPage, setCurrentPage] = useState(0);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [chatToDelete, setChatToDelete] = useState<ChatView | null>(null);
    const pageSize = 10;

    const { data: agentData, isLoading: agentLoading } = useAgent(agentId || '', !!agentId);
    const { data: agentChatsData, isLoading: chatsLoading } = useAgentChats({
        view: 'mine',
        stereo: 'root',
        agentId: agentId || '',
        page: currentPage,
        limit: pageSize,
    });

    const deleteChatMutation = useDeleteChat();
    const isDeleting = deleteChatMutation.isPending;

    const totalCount = agentChatsData?.total || 0;
    const totalPages = Math.ceil(totalCount / pageSize);
    const chats = agentChatsData?.data || [];

    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
    };

    const handleChatClick = (chatId: string) => {
        if (agentId) {
            if (workspaceId && projectId) {
                navigate(`/w/${workspaceId}/p/${projectId}/my-agents/${agentId}/chats/${chatId}`);
            } else {
                navigate(`/my-agents/${agentId}/chats/${chatId}`);
            }
        }
        onChatSelect?.(chatId);
        onOpenChange(false);
    };

    const handleDeleteClick = (chat: ChatView, e: React.MouseEvent) => {
        e.stopPropagation();
        setChatToDelete(chat);
        setIsDeleteDialogOpen(true);
    };

    const handleDelete = async () => {
        if (!chatToDelete?.id) {
            return;
        }

        try {
            await deleteChatMutation.mutateAsync(chatToDelete.id);
            // 캐시에서 삭제
            updateAllListCaches({ resource: 'myChatbotKeys', queryClient }, (oldData: any) => {
                if (!oldData?.data) {
                    return oldData;
                }
                const filteredData = oldData.data.filter((chat: any) => chat.id !== chatToDelete.id);
                return {
                    ...oldData,
                    data: filteredData,
                    list: filteredData,
                    total: Math.max((oldData.total || 0) - 1, 0),
                };
            });
            toast({
                title: t('agent.chat.list.messages.deleteSuccess.title'),
                description: t('agent.chat.list.messages.deleteSuccess.description', {
                    chatName: chatToDelete.name || t('agent.chat.list.defaultChatName'),
                }),
            });
            setIsDeleteDialogOpen(false);
            setChatToDelete(null);
            // Refresh the chat list
            if (agentId) {
                await queryClient.invalidateQueries({
                    queryKey: ['agentChats', { agentId, view: 'mine', stereo: 'root' }],
                });
            }
        } catch (error) {
            console.error('Error deleting chat:', error);
            toast({
                title: t('agent.chat.list.messages.deleteError.title'),
                description: t('agent.chat.list.messages.deleteError.description'),
                variant: 'destructive',
            });
        }
    };

    const formatChatDate = (timestamp: number) => {
        return formatDate(timestamp, 'YY.MM.DD HH:mm');
    };

    const getPaginationRange = () => {
        const range = [];
        const maxVisible = 5;
        let start = Math.max(0, currentPage - Math.floor(maxVisible / 2));
        const end = Math.min(totalPages - 1, start + maxVisible - 1);

        if (end - start + 1 < maxVisible) {
            start = Math.max(0, end - maxVisible + 1);
        }

        if (start > 0) {
            range.push('...');
        }

        for (let i = start; i <= end; i++) {
            range.push(i);
        }

        if (end < totalPages - 1) {
            range.push('...');
        }

        return range;
    };

    return (
        <>
            <Sheet open={isOpen} onOpenChange={onOpenChange}>
                <SheetContent
                    className={`w-[500px] max-md:w-full overflow-hidden flex flex-col ${
                        isDeleteDialogOpen ? 'z-10' : 'z-[1000]'
                    }`}
                >
                    <SheetHeader>
                        <SheetTitle>{t('agent.chat.list.title')}</SheetTitle>
                        <SheetClose>
                            <X className="w-5 h-5" />
                        </SheetClose>
                    </SheetHeader>

                    {/* Agent Info */}
                    {agentData && (
                        <Card className="px-[13px] py-[9px] pb-3  rounded-xl">
                            <div className="text-base font-medium">
                                {agentData.meta$?.nick || agentData.id || t('agent.listItem.states.noName')}
                            </div>
                            <div className="text-xs text-sub font-medium">
                                {agentData.meta$?.role || t('agent.listItem.states.noRole')}
                            </div>
                            <div className="h-px w-full bg-detail-line my-[10px]"></div>
                            <div className="flex flex-col space-y-[10px]">
                                {agentData.brain$?.name && (
                                    <div className="flex items-center justify-between text-sm font-medium">
                                        {t('agent.chat.list.model')}
                                        <span className="text-sub">{agentData.brain$.name}</span>
                                    </div>
                                )}
                                {agentData.embedding && (
                                    <div className="flex items-center justify-between text-sm font-medium">
                                        {t('agent.chat.list.embedding')}
                                        <span className="text-sub">{agentData.embedding}</span>
                                    </div>
                                )}
                            </div>
                        </Card>
                    )}

                    <SheetDescription className="text-start text-sub text-sm">
                        {t('agent.chat.list.description')}
                    </SheetDescription>

                    <div className="flex-1 overflow-hidden flex flex-col">
                        {chatsLoading ? (
                            <div className="flex items-center justify-center h-full">
                                <Loader2 className="h-8 w-8 animate-spin" />
                            </div>
                        ) : chats.length === 0 ? (
                            <div className="flex items-center justify-center h-full">
                                <div className="text-center text-sub">
                                    <MessageSquare className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                                    <div className="text-lg font-medium mb-2">
                                        {t('agent.chat.list.emptyState.title')}
                                    </div>
                                    <div className="text-sm">{t('agent.chat.list.emptyState.description')}</div>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="flex-1 overflow-auto">
                                    <div className="space-y-2">
                                        {chats.map((chat: ChatView) => (
                                            <div
                                                key={chat.id}
                                                className="w-full bg-card border border-border rounded-lg flex flex-col px-4 py-3 hover:border-point transition-colors cursor-pointer"
                                                onClick={() => chat.id && handleChatClick(chat.id)}
                                            >
                                                <div className="flex items-start justify-between mb-2">
                                                    <button className="flex items-center gap-3 flex-1 min-w-0 text-left">
                                                        <Avatar className="w-8 h-8">
                                                            <Image
                                                                src={AiLogoImage}
                                                                darkSrc={AiLogoDarkImage}
                                                                className="w-5 h-5"
                                                            />
                                                        </Avatar>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="font-medium text-sm truncate">
                                                                {chat.name || t('agent.chat.list.defaultChatName')}
                                                            </div>
                                                            <div className="text-xs text-sub">
                                                                {formatChatDate(chat.createdAt || 0)}
                                                            </div>
                                                        </div>
                                                    </button>
                                                    <DropdownMenu>
                                                        <DropdownMenuTrigger asChild>
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                disabled={isDeleting}
                                                                className="p-1 h-6 w-6 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors z-10"
                                                                onClick={e => e.stopPropagation()}
                                                            >
                                                                <EllipsisVertical className="w-3 h-3" />
                                                            </Button>
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent
                                                            align="end"
                                                            className="z-[9999] w-[100px] px-3 py-[6px] rounded-lg"
                                                        >
                                                            <DropdownMenuItem
                                                                onClick={e => handleDeleteClick(chat, e)}
                                                                className="text-xs text-red-600 focus:text-red-600 focus:bg-red-50 dark:focus:bg-red-950"
                                                            >
                                                                <Trash2 className="w-4 h-4 mr-2" />
                                                                {t('agent.chat.list.actions.deleteMenuItem')}
                                                            </DropdownMenuItem>
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                </div>
                                                <div className="text-xs text-sub space-y-1">
                                                    <div className="flex flex-wrap gap-2">
                                                        {chat.brain$?.name && (
                                                            <span>
                                                                {t('agent.chat.list.model')}: {chat.brain$.name}
                                                            </span>
                                                        )}
                                                        {chat.embedding && (
                                                            <span>
                                                                {t('agent.chat.list.embedding')}: {chat.embedding}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="flex flex-wrap gap-2">
                                                        {chat.strategy && (
                                                            <span>
                                                                {t('agent.chat.list.strategy')}: {chat.strategy}
                                                            </span>
                                                        )}
                                                        {chat.profile$?.name && (
                                                            <span>
                                                                {t('agent.chat.list.participant')}: {chat.profile$.name}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {totalPages > 1 && (
                                    <div className="flex items-center justify-center space-x-2 p-4 border-t border-border flex-shrink-0">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            disabled={currentPage === 0}
                                            className="h-8 w-8 p-0"
                                        >
                                            <ChevronLeft className="h-4 w-4" />
                                        </Button>

                                        {getPaginationRange().map((pageNumber, idx) => (
                                            <Button
                                                key={idx}
                                                variant={pageNumber === currentPage ? 'default' : 'outline'}
                                                size="sm"
                                                onClick={() =>
                                                    typeof pageNumber === 'number' && handlePageChange(pageNumber)
                                                }
                                                disabled={typeof pageNumber !== 'number'}
                                                className="h-8 min-w-8 px-2"
                                            >
                                                {typeof pageNumber === 'number' ? pageNumber + 1 : '...'}
                                            </Button>
                                        ))}

                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            disabled={currentPage >= totalPages - 1}
                                            className="h-8 w-8 p-0"
                                        >
                                            <ChevronRight className="h-4 w-4" />
                                        </Button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </SheetContent>
            </Sheet>

            {/* Delete Confirmation Dialog */}
            <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
                <AlertDialogContent className="z-[99999]">
                    <AlertDialogTitle>{t('agent.chat.list.dialog.title')}</AlertDialogTitle>
                    <AlertDialogDescription>{t('agent.chat.list.dialog.warning')}</AlertDialogDescription>
                    <AlertDialogFooter>
                        <AlertDialogCancel type="button" onClick={() => setIsDeleteDialogOpen(false)}>
                            {t('common.cancel')}
                        </AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} disabled={deleteChatMutation.isPending}>
                            {deleteChatMutation.isPending ? (
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                            ) : (
                                t('agent.chat.list.dialog.actions.delete')
                            )}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};
